import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import SafetyShell from "../safety/SafetyShell";
import { getRecents, pushRecent, clearRecents, togglePin as toggleRecentPin } from "../safety/recents";
import {
  SAFETY_CATEGORIES,
  accessibleModules,
  MODULE_BY_KEY,
  catColor,
} from "../safety/modules";
import {
  Package,
  QrCode,
  Upload,
  Clock,
  CheckCircle2,
  Activity,
  FileText,
  ChevronRight,
  Plus,
  AlertTriangle,
  Star,
  X,
  RefreshCw,
  GripVertical,
  Sparkles,
  LayoutGrid,
  Search,
  ChevronDown,
  RotateCcw,
  Bell,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

// --- Home-only localStorage helpers (per browser): module visit counts + last visit ---
const VISITS_KEY = "safety:module-visits";
const LASTVISIT_KEY = "safety:last-visit";
const getVisits = () => { try { return JSON.parse(localStorage.getItem(VISITS_KEY) || "{}"); } catch { return {}; } };
const bumpVisit = (key) => { const v = getVisits(); v[key] = (v[key] || 0) + 1; localStorage.setItem(VISITS_KEY, JSON.stringify(v)); };
const COLLAPSE_KEY = "safety:collapsed-cats";
const getCollapsed = () => { try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || "[]"); } catch { return []; } };

const fmtAgo = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  const diff = Date.now() - date.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  const h = Math.floor(m / 60);
  if (h < 1) return `${m} min ago`;
  const days = Math.floor(h / 24);
  if (days < 1) return `${h} hr ago`;
  return `${days} d ago`;
};

const StatusBadge = ({ status }) => {
  const map = {
    pending_approval: ["sf-badge sf-badge-pending", "pending"],
    approved: ["sf-badge sf-badge-approved", "approved"],
    draft: ["sf-badge sf-badge-draft", "draft"],
    rejected: ["sf-badge sf-badge-rejected", "rejected"],
    pending: ["sf-badge sf-badge-pending", "pending"],
    complete: ["sf-badge sf-badge-approved", "complete"],
    partial: ["sf-badge sf-badge-pending", "partial"],
  };
  const [cls, label] = map[status] || ["sf-badge sf-badge-draft", status || "—"];
  return <span className={cls}>{label}</span>;
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

function SafetyDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [stats, setStats] = useState({ pending_approvals: 0, today_coated_kg: 0, today_reports: 0, active_pos: 0, qr_batches: 0 });
  const [recentReports, setRecentReports] = useState([]);
  const [pendingTracker, setPendingTracker] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [recentAmendments, setRecentAmendments] = useState([]);
  const [recents, setRecents] = useState(() => getRecents());
  const [recomputing, setRecomputing] = useState(false);

  // My Modules pins
  const [pins, setPins] = useState({ hasPersonal: false, pinned: null, defaults: [], hiddenCategories: [] });
  const [editPins, setEditPins] = useState(false);
  const [dragKey, setDragKey] = useState(null);
  const [badges, setBadges] = useState({});
  const [moduleFilter, setModuleFilter] = useState("");
  const [collapsedCats, setCollapsedCats] = useState(() => getCollapsed());
  const [lastVisit, setLastVisit] = useState(null);

  const toggleCat = (key) => setCollapsedCats((c) => {
    const next = c.includes(key) ? c.filter((k) => k !== key) : [...c, key];
    localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next));
    return next;
  });

  useEffect(() => {
    const onChange = () => setRecents(getRecents());
    window.addEventListener("safety:recents-changed", onChange);
    return () => window.removeEventListener("safety:recents-changed", onChange);
  }, []);

  useEffect(() => {
    fetchAll();
    // Last-visit (per browser) for a lightweight security-awareness line
    const prev = localStorage.getItem(LASTVISIT_KEY);
    if (prev) setLastVisit(prev);
    localStorage.setItem(LASTVISIT_KEY, new Date().toISOString());
  }, []);

  const fetchAll = async () => {
    try {
      const [permRes, posRes, batchesRes, reportsRes, prefsRes, badgeRes] = await Promise.all([
        axios.get(`${API}/auth/me/permissions`, auth()),
        axios.get(`${API}/po/list`, auth()).catch(() => ({ data: [] })),
        axios.get(`${API}/qr/batches`, auth()).catch(() => ({ data: [] })),
        axios.get(`${API}/production/reports`, auth()).catch(() => ({ data: [] })),
        axios.get(`${API}/safety/prefs`, auth()).catch(() => ({ data: { hasPersonal: false, pinned: null, defaults: [] } })),
        axios.get(`${API}/safety/home-badges`, auth()).catch(() => ({ data: {} })),
      ]);
      setPermissions(permRes.data.effective_permissions || []);
      setPins({
        hasPersonal: !!prefsRes.data.has_personal,
        pinned: prefsRes.data.pinned || null,
        defaults: prefsRes.data.defaults || [],
        hiddenCategories: prefsRes.data.hidden_categories || [],
      });
      setBadges(badgeRes.data || {});

      const pos = posRes.data || [];
      const reports = reportsRes.data || [];
      const today = new Date().toISOString().slice(0, 10);
      const todayReports = reports.filter((r) => r.report_date === today);
      const todayKg = todayReports.reduce(
        (s, r) => s + ((r.po_consumptions || []).reduce((a, c) => a + Number(c.consumption_kg || 0), 0) || 0), 0);

      setStats({
        pending_approvals: reports.filter((r) => r.status === "pending_approval").length,
        today_coated_kg: Math.round(todayKg * 10) / 10,
        today_reports: todayReports.length,
        active_pos: pos.filter((p) => p.status !== "complete" && p.status !== "completed").length,
        qr_batches: (batchesRes.data || []).length,
      });
      setRecentReports(reports.slice(0, 6));

      try {
        const pendRes = await axios.get(`${API}/production/pending-summary`, auth());
        const list = (pendRes.data?.pos || []).filter((p) => (p.pending_total ?? 0) > 0)
          .sort((a, b) => (b.pending_total || 0) - (a.pending_total || 0));
        setPendingTracker(list.slice(0, 5));
      } catch { setPendingTracker([]); }
      try {
        const aud = await axios.get(`${API}/audit/logs?page=1&page_size=8`, auth());
        setAuditEvents(aud.data?.logs || []);
      } catch { setAuditEvents([]); }
      try {
        const amd = await axios.get(`${API}/po/recent-amendments?limit=6`, auth());
        setRecentAmendments(amd.data?.items || []);
      } catch { setRecentAmendments([]); }
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const recomputePoStatuses = async () => {
    if (!window.confirm("Recompute PO statuses for ALL POs based on approved Production Reports? This is safe to run anytime.")) return;
    setRecomputing(true);
    try {
      const res = await axios.post(`${API}/po/recompute-statuses`, {}, auth());
      const { scanned, changed, counts, changes } = res.data || {};
      if (changed === 0) {
        toast.success(`All ${scanned} PO statuses already in sync · pending: ${counts?.pending ?? 0} · partial: ${counts?.partial ?? 0} · complete: ${counts?.complete ?? 0}`);
      } else {
        const sample = (changes || []).slice(0, 3).map((c) => `${c.po_number}: ${c.from} → ${c.to}`).join(", ");
        toast.success(`Updated ${changed}/${scanned} POs · ${sample}${changes.length > 3 ? ` …+${changes.length - 3} more` : ""}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to recompute statuses");
    } finally { setRecomputing(false); }
  };

  const has = (p) => permissions.includes(p);
  const accessible = accessibleModules(permissions);
  const accessibleKeys = new Set(accessible.map((m) => m.key));
  const currentPins = (pins.hasPersonal ? pins.pinned : pins.defaults) || [];
  const pinnedModules = currentPins.filter((k) => accessibleKeys.has(k)).map((k) => MODULE_BY_KEY[k]).filter(Boolean);
  const isPinned = (key) => currentPins.includes(key);

  const savePins = async (keys) => {
    setPins((p) => ({ ...p, hasPersonal: true, pinned: keys }));
    try { await axios.put(`${API}/safety/prefs`, { pinned: keys }, auth()); }
    catch { toast.error("Couldn't save your pins"); }
  };
  const togglePin = (key) => {
    const next = currentPins.includes(key) ? currentPins.filter((k) => k !== key) : [...currentPins, key];
    savePins(next);
  };
  const onDrop = (targetKey) => {
    if (!dragKey || dragKey === targetKey) { setDragKey(null); return; }
    const arr = [...currentPins];
    const from = arr.indexOf(dragKey);
    const to = arr.indexOf(targetKey);
    if (from < 0 || to < 0) { setDragKey(null); return; }
    arr.splice(to, 0, arr.splice(from, 1)[0]);
    setDragKey(null);
    savePins(arr);
  };

  const resetToRecommended = () => {
    savePins(pins.defaults || []);
    toast.success("My Modules reset to your role's recommended set");
  };

  // Live count badges (from /safety/home-badges + local data)
  const badgeFor = (key) => {
    if (key === "approvals" && (badges.approvals || 0) > 0) return { text: `${badges.approvals} waiting`, tone: "" };
    if (key === "shipping_bills" && (badges.shipping_unmatched || 0) > 0) return { text: `${badges.shipping_unmatched} unmatched`, tone: "" };
    if (key === "stock_count" && (badges.stock_unidentified || 0) > 0) return { text: `${badges.stock_unidentified} unidentified`, tone: "" };
    if (key === "packing_lists" && (badges.packing_unparsed || 0) > 0) return { text: `${badges.packing_unparsed} unparsed`, tone: "info" };
    if (key === "po") {
      const flagged = recentAmendments.filter((a) => a.review_flagged).length;
      if (flagged > 0) return { text: `${flagged} to review`, tone: "" };
      if (recentAmendments.length > 0) return { text: `${recentAmendments.length} amended`, tone: "info" };
    }
    return null;
  };

  // "For you" — role-aware KPI picks
  const flaggedAmendments = recentAmendments.filter((a) => a.review_flagged).length;
  const kpiCandidates = [
    { show: has("production.approve"), priority: 1, label: "Pending Approvals", icon: Clock, value: stats.pending_approvals, hint: "Awaiting your sign-off", route: "/safety/production-reports/approvals", alert: stats.pending_approvals > 0, pill: stats.pending_approvals > 0 ? "action" : null },
    { show: has("po.view") && flaggedAmendments > 0, priority: 2, label: "PO Amendments", icon: RefreshCw, value: flaggedAmendments, hint: "Replaced/restored — review", route: "/safety/po?amended=1", alert: true, pill: "review" },
    { show: pendingTracker.length > 0, priority: 3, label: "Top Pending PO", icon: AlertTriangle, value: pendingTracker[0]?.pending_total ?? 0, hint: pendingTracker[0]?.po_number || "", route: "/safety/pending-production", alert: false },
    { show: true, priority: 4, label: "Active POs", icon: Package, value: stats.active_pos, hint: "In progress or pending", route: "/safety/po" },
    { show: true, priority: 5, label: "Today's Production", icon: Activity, value: stats.today_coated_kg, unit: "kg", hint: `${stats.today_reports} reports today`, route: "/safety/production-reports" },
    { show: true, priority: 6, label: "QR Batches", icon: QrCode, value: stats.qr_batches, hint: "Lifetime generated", route: "/safety/history" },
  ].filter((k) => k.show).sort((a, b) => a.priority - b.priority).slice(0, 4);

  const go = (m) => {
    bumpVisit(m.key);
    pushRecent({ type: "module", id: m.key, label: m.label, sublabel: "", path: m.route });
    navigate(m.route);
  };

  // #13 hide admin-hidden categories; #5 module search filter
  const hiddenCats = pins.hiddenCategories || [];
  const visibleCategories = SAFETY_CATEGORIES.filter((c) => !hiddenCats.includes(c.key));
  const filterQ = moduleFilter.trim().toLowerCase();
  const matchModule = (m) => !filterQ || m.label.toLowerCase().includes(filterQ) || m.desc.toLowerCase().includes(filterQ);

  // #3 auto-suggest a pin from usage: most-visited accessible, unpinned, not hidden
  const visits = getVisits();
  const suggestion = accessible
    .filter((m) => !hiddenCats.includes(m.cat) && !currentPins.includes(m.key) && (visits[m.key] || 0) >= 3)
    .sort((a, b) => (visits[b.key] || 0) - (visits[a.key] || 0))[0] || null;

  // #2 needs-attention items (only shown when > 0 and user can act)
  const attention = [
    has("production.approve") && (badges.approvals || 0) > 0 ? { label: "approvals", n: badges.approvals, route: "/safety/production-reports/approvals" } : null,
    has("packing_list.view") && (badges.shipping_unmatched || 0) > 0 ? { label: "unmatched docs", n: badges.shipping_unmatched, route: "/safety/shipping-bills" } : null,
    has("inventory.view") && (badges.stock_unidentified || 0) > 0 ? { label: "unidentified pallets", n: badges.stock_unidentified, route: "/safety/inventory/stock-count" } : null,
    has("packing_list.view") && (badges.packing_unparsed || 0) > 0 ? { label: "unparsed lists", n: badges.packing_unparsed, route: "/safety/packing-lists" } : null,
    recentAmendments.filter((a) => a.review_flagged).length > 0 ? { label: "POs to review", n: recentAmendments.filter((a) => a.review_flagged).length, route: "/safety/po?amended=1" } : null,
  ].filter(Boolean);

  // Right rail: recents + quick actions + audit
  const rightRail = (
    <div className="space-y-4">
      {recents.length > 0 && (
        <div className="sf-right-rail" data-testid="rail-recents">
          <div className="flex items-center justify-between mb-1">
            <h4 className="flex items-center gap-1.5"><Star className="w-3 h-3" /> Recently Visited</h4>
            <button onClick={() => clearRecents()} className="text-[10px] text-zinc-400 hover:text-zinc-700" data-testid="rail-recents-clear" title="Clear unpinned"><X className="w-3 h-3" /></button>
          </div>
          <ul className="space-y-1 mt-2">
            {recents.slice(0, 6).map((r, i) => (
              <li key={`${r.type}:${r.id}`} className="group flex items-stretch">
                <button onClick={() => navigate(r.path)} className="flex-1 min-w-0 flex items-start gap-2 px-2 py-1.5 hover:bg-zinc-50 text-left" data-testid={`rail-recent-${i}`} title={r.label}>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400 sf-mono mt-0.5 w-12 shrink-0">{r.type}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-medium text-zinc-800 truncate">{r.label}</span>
                    {r.sublabel && <span className="block text-[10px] text-zinc-500 truncate">{r.sublabel}</span>}
                  </span>
                </button>
                <button onClick={(e) => { e.stopPropagation(); toggleRecentPin(`${r.type}:${r.id}`); }} className={`px-1.5 transition-opacity ${r.pinned ? "text-orange-600 opacity-100" : "text-zinc-300 opacity-0 group-hover:opacity-100 hover:text-zinc-700"}`} data-testid={`rail-recent-pin-${i}`}>
                  <Star className={`w-3 h-3 ${r.pinned ? "fill-orange-600" : ""}`} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="sf-right-rail" data-testid="rail-shortcuts">
        <h4>Quick Actions</h4>
        <div className="space-y-1">
          {has("po.upload") && <button onClick={() => navigate("/safety/po")} className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50 text-left" data-testid="rail-upload-po"><Upload className="w-4 h-4 text-zinc-500" /> Upload PO</button>}
          {has("qr.generate") && <button onClick={() => navigate("/safety/qr")} className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50 text-left" data-testid="rail-generate-qr"><QrCode className="w-4 h-4 text-zinc-500" /> Generate QR</button>}
          <button onClick={() => navigate("/safety/production-reports/new")} className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50 text-left" data-testid="rail-new-report"><Plus className="w-4 h-4 text-zinc-500" /> New Production Report</button>
          <button onClick={() => navigate("/safety/production-live")} className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50 text-left" data-testid="rail-production-live"><Activity className="w-4 h-4 text-emerald-600" /> Live Production</button>
          {has("production.delete_protected") && (
            <button onClick={recomputePoStatuses} disabled={recomputing} className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50 text-left disabled:opacity-50" data-testid="rail-recompute-po-statuses" title="Heal PO statuses by recomputing from approved production reports">
              <RefreshCw className={`w-4 h-4 text-zinc-500 ${recomputing ? "animate-spin" : ""}`} /> {recomputing ? "Recomputing…" : "Recompute PO Statuses"}
            </button>
          )}
        </div>
      </div>

      <div className="sf-right-rail" data-testid="rail-audit">
        <div className="flex items-center justify-between mb-1">
          <h4>Audit Timeline</h4>
          {has("audit.view") && <button onClick={() => navigate("/audit")} className="text-[10px] text-zinc-500 hover:text-zinc-900" data-testid="rail-audit-full">Full log →</button>}
        </div>
        {auditEvents.length === 0 ? <p className="text-xs text-zinc-400 py-2">No recent events</p> : (
          <ul className="space-y-2 mt-2">
            {auditEvents.slice(0, 6).map((e, i) => (
              <li key={i} className="text-xs leading-snug">
                <div className="flex items-baseline gap-2"><span className="sf-mono text-[10px] text-zinc-400">{fmtAgo(e.timestamp)}</span><span className="text-zinc-800 truncate">{e.action || "event"}</span></div>
                <div className="text-[10px] text-zinc-500 truncate">{e.user_email || "—"}{e.target_name ? ` · ${e.target_name}` : ""}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  const miniMap = [
    { label: "Safety", to: "/safety", active: true, depth: 0 },
    { label: "Customers", to: "/safety/customers", depth: 1 },
    { label: "Purchase Orders", to: "/safety/po", depth: 1 },
    { label: "Production Reports", to: "/safety/production-reports", depth: 1 },
    { label: "Invoice cum Packing Lists", to: "/safety/packing-lists", depth: 1 },
    { label: "Pallets", to: "/safety/pallets", depth: 1 },
    { label: "Powder Batches", to: "/safety/powder-masters", depth: 1 },
    { label: "QR Batches", to: "/safety/history", depth: 1 },
  ];

  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[{ label: "Safety", to: "/safety" }, { label: "Home" }]}
      miniMap={miniMap}
      rightRail={rightRail}
    >
      {loading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-4">{[0, 1, 2, 3].map((i) => <div key={i} className="sf-kpi animate-pulse"><div className="h-3 w-16 bg-zinc-200" /><div className="h-8 w-24 bg-zinc-200" /></div>)}</div>
          <div className="sf-surface h-64 animate-pulse" />
        </div>
      ) : (
        <>
          {/* NEEDS ATTENTION BAR (#2) */}
          {attention.length > 0 && (
            <div className="sf-attention" data-testid="sf-attention-bar">
              <span className="flex items-center gap-1.5 text-sm font-bold text-orange-700"><Bell className="w-4 h-4" /> Needs attention</span>
              {attention.map((a, i) => (
                <button key={i} className="sf-attention-chip" onClick={() => navigate(a.route)} data-testid={`sf-attention-${i}`}>
                  <span className="n">{a.n}</span> {a.label}
                </button>
              ))}
            </div>
          )}

          {/* GREETING */}
          <div className="flex items-end justify-between mb-2" data-testid="sf-greeting">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight" style={{ fontFamily: "var(--sf-font-heading)" }}>
                {greeting()}, {(user?.name || user?.email || "there").split(" ")[0].split("@")[0]}
              </h1>
              <div className="text-sm text-zinc-500 mt-1">
                {new Date().toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", weekday: "long", day: "numeric", month: "long" })}
                {" · "}
                <span className="sf-access-chip" data-testid="sf-access-chip">{accessible.length} modules unlocked</span>
                {lastVisit && <span className="text-zinc-400"> · last visit {fmtAgo(lastVisit)}</span>}
              </div>
            </div>
            <button onClick={() => setEditPins((v) => !v)} className="text-sm border border-zinc-200 bg-white hover:bg-zinc-50 rounded-md px-3 py-2 flex items-center gap-2" data-testid="sf-customize-btn">
              <Star className={`w-4 h-4 ${editPins ? "fill-orange-500 text-orange-500" : "text-zinc-500"}`} /> {editPins ? "Done" : "Customize"}
            </button>
          </div>

          {/* MY MODULES */}
          <div className="sf-section-label"><Star className="w-3 h-3 text-orange-500" /> My Modules
            <span className="tag">{editPins ? "tap ★ on cards below to pin · drag to reorder" : "pinned quick-launch"}</span>
            {editPins && (pins.defaults || []).length > 0 && (
              <button onClick={resetToRecommended} className="ml-auto text-xs text-orange-600 flex items-center gap-1" data-testid="sf-reset-pins">
                <RotateCcw className="w-3 h-3" /> Reset to recommended
              </button>
            )}
          </div>
          <div className="flex gap-2.5 flex-wrap items-center" data-testid="sf-my-modules">
            {pinnedModules.length === 0 && (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-zinc-400">No pins yet — pin your most-used modules for one-tap access.</span>
                {(pins.defaults || []).length > 0 && (
                  <button onClick={resetToRecommended} className="sf-suggest" data-testid="sf-starter-pins"><Star className="w-3.5 h-3.5" /> Use recommended set</button>
                )}
              </div>
            )}
            {pinnedModules.map((m) => {
              const Icon = m.icon;
              return (
                <div
                  key={m.key}
                  className={`sf-chip ${dragKey === m.key ? "dragging" : ""}`}
                  draggable={editPins}
                  onDragStart={() => setDragKey(m.key)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(m.key)}
                  onClick={() => !editPins && go(m)}
                  data-testid={`sf-pin-${m.key}`}
                >
                  {editPins && <GripVertical className="w-3.5 h-3.5 text-zinc-300" />}
                  <span className="sf-chip-ic" style={{ background: catColor(m.cat) }}><Icon className="w-3.5 h-3.5" /></span>
                  {m.label}
                  {editPins && <button onClick={(e) => { e.stopPropagation(); togglePin(m.key); }} className="text-orange-500 ml-1" data-testid={`sf-unpin-${m.key}`}><Star className="w-3.5 h-3.5 fill-orange-500" /></button>}
                </div>
              );
            })}
            {/* #3 auto-suggest a pin from usage */}
            {!editPins && suggestion && (
              <button className="sf-suggest" onClick={() => togglePin(suggestion.key)} data-testid="sf-suggest-pin">
                <Plus className="w-3.5 h-3.5" /> Pin {suggestion.label}?
              </button>
            )}
          </div>

          {/* #8 recently visited chips */}
          {!editPins && recents.filter((r) => r.type === "module").length > 0 && (
            <div className="flex gap-2 flex-wrap mt-2" data-testid="sf-recent-chips">
              <span className="text-[11px] uppercase tracking-wider text-zinc-400 self-center">Recent:</span>
              {recents.filter((r) => r.type === "module").slice(0, 4).map((r, i) => (
                <button key={r.id} onClick={() => navigate(r.path)} className="text-xs text-zinc-600 border border-zinc-200 rounded-full px-3 py-1 hover:border-orange-400 hover:text-orange-600" data-testid={`sf-recent-chip-${i}`}>
                  {r.label}
                </button>
              ))}
            </div>
          )}

          {/* FOR YOU */}
          <div className="sf-section-label"><Sparkles className="w-3 h-3" /> For you <span className="tag">picked for your role</span></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="sf-for-you">
            {kpiCandidates.map((k, i) => {
              const Icon = k.icon;
              return (
                <button key={i} className={`sf-kpi text-left relative ${k.alert ? "alert" : ""}`} onClick={() => navigate(k.route)} data-testid={`sf-kpi-${i}`}>
                  {k.pill && <span className="absolute top-3 right-3 text-[10px] font-bold rounded-full px-2 py-0.5" style={{ background: "var(--sf-accent-soft)", color: "var(--sf-accent)" }}>{k.pill}</span>}
                  <span className="sf-label flex items-center gap-1.5"><Icon className="w-3 h-3" /> {k.label}</span>
                  <span className="sf-kpi-value" style={{ color: k.alert ? "var(--sf-accent)" : "var(--sf-text-primary)" }}>
                    {k.value}{k.unit && <span className="text-sm text-zinc-500 font-sans ml-1">{k.unit}</span>}
                  </span>
                  <span className="text-xs text-zinc-500 truncate block">{k.hint}</span>
                </button>
              );
            })}
          </div>

          {/* LAUNCHPAD */}
          <div className="sf-section-label"><LayoutGrid className="w-3 h-3" /> Launchpad <span className="tag">only modules you can access</span>
            <div className="ml-auto flex items-center gap-2 relative">
              <Search className="w-3.5 h-3.5 text-zinc-400 absolute left-3 pointer-events-none" />
              <input className="sf-launch-search" style={{ paddingLeft: 30 }} placeholder="Filter modules…" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)} data-testid="sf-launch-filter" />
            </div>
          </div>
          {visibleCategories.map((cat) => {
            const mods = accessible.filter((m) => m.cat === cat.key && matchModule(m));
            if (mods.length === 0) return null;
            const collapsed = collapsedCats.includes(cat.key);
            return (
              <div key={cat.key} className="mb-2" data-testid={`sf-cat-${cat.key}`}>
                <div className="sf-cat-label">
                  <span className="w-2 h-2 rounded-sm inline-block" style={{ background: cat.color }} /> {cat.label}
                  <button className="sf-cat-toggle" onClick={() => toggleCat(cat.key)} data-testid={`sf-cat-toggle-${cat.key}`} title={collapsed ? "Expand" : "Collapse"}>
                    <ChevronDown className={`w-4 h-4 transition-transform ${collapsed ? "-rotate-90" : ""}`} />
                  </button>
                </div>
                {!collapsed && (
                  <div className="sf-launch-grid">
                    {mods.map((m) => {
                      const Icon = m.icon;
                      const badge = badgeFor(m.key);
                      const pinned = isPinned(m.key);
                      return (
                        <div key={m.key} className="sf-launch-card" onClick={() => go(m)} data-testid={`sf-launch-${m.key}`}>
                          <button
                            className={`sf-launch-pin ${pinned ? "on" : ""}`}
                            onClick={(e) => { e.stopPropagation(); togglePin(m.key); }}
                            title={pinned ? "Unpin from My Modules" : "Pin to My Modules"}
                            data-testid={`sf-launch-pin-${m.key}`}
                          >
                            <Star className={`w-4 h-4 ${pinned ? "fill-orange-500" : ""}`} />
                          </button>
                          <div className="sf-launch-ic" style={{ background: cat.color }}><Icon className="w-5 h-5" /></div>
                          <h3>{m.label}</h3>
                          <p>{m.desc}</p>
                          {badge && <span className={`sf-launch-badge ${badge.tone === "info" ? "info" : ""}`}><Activity className="w-3 h-3" /> {badge.text}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* WORK SURFACES (kept from before) */}
          <div className="sf-section-label"><FileText className="w-3 h-3" /> Your work</div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="sf-surface lg:col-span-1" data-testid="bento-pending-tracker">
              <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
                <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-orange-600" /><h2 className="text-sm font-bold tracking-tight">Pending Qty Tracker</h2></div>
                <button onClick={() => navigate("/safety/pending-production")} className="text-xs sf-link-lateral" data-testid="tracker-open-full">Open full →</button>
              </div>
              {pendingTracker.length === 0 ? (
                <div className="p-6 text-center text-zinc-400 text-sm"><CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-zinc-300" />Nothing pending. All clear.</div>
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {pendingTracker.map((r, i) => (
                    <li key={i} className="px-4 py-2.5 sf-row-clickable flex items-center justify-between"
                      onClick={() => { const target = r.po_id ? `/safety/po/${r.po_id}/detail` : "/safety/pending-production"; pushRecent({ type: "po", id: r.po_id || r.po_number, label: `PO ${r.po_number}`, sublabel: r.customer_name || "", path: target }); navigate(target); }}
                      data-testid={`tracker-row-${i}`}>
                      <div className="min-w-0"><div className="sf-mono text-sm font-medium truncate">{r.po_number || r.po_id}</div><div className="text-xs text-zinc-500 truncate">{r.customer_name || ""}</div></div>
                      <div className="text-right"><div className="sf-mono text-base font-semibold" style={{ color: "var(--sf-accent)" }}>{r.pending_total ?? 0}</div><div className="text-[10px] text-zinc-400 uppercase tracking-wider">pending</div></div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="sf-surface lg:col-span-2" data-testid="bento-recent-reports">
              <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
                <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-zinc-700" /><h2 className="text-sm font-bold tracking-tight">Recent Production Reports</h2></div>
                <button onClick={() => navigate("/safety/production-reports")} className="text-xs sf-link-lateral" data-testid="reports-open-all">View all →</button>
              </div>
              {recentReports.length === 0 ? <div className="p-6 text-center text-zinc-400 text-sm">No reports yet.</div> : (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-zinc-500">
                    <th className="px-4 py-2 sf-label font-semibold">Date</th><th className="px-4 py-2 sf-label font-semibold">PO(s)</th><th className="px-4 py-2 sf-label font-semibold">Customer</th><th className="px-4 py-2 sf-label font-semibold">Hrs</th><th className="px-4 py-2 sf-label font-semibold">Status</th><th className="px-4 py-2"></th>
                  </tr></thead>
                  <tbody>
                    {recentReports.map((r) => (
                      <tr key={r.id} className="border-t border-zinc-100 sf-row-clickable"
                        onClick={() => { pushRecent({ type: "report", id: r.id, label: `Report ${r.report_date}`, sublabel: (r.po_numbers || []).join(", ") || "", path: `/safety/production-reports/${r.id}` }); navigate(`/safety/production-reports/${r.id}`); }}
                        data-testid={`recent-report-row-${r.id}`}>
                        <td className="px-4 py-2.5 sf-mono">{r.report_date}</td>
                        <td className="px-4 py-2.5 sf-mono text-xs truncate max-w-[140px]">{(r.po_numbers || []).join(", ") || "—"}</td>
                        <td className="px-4 py-2.5 truncate max-w-[160px]">{(r.customer_names || []).join(", ") || "—"}</td>
                        <td className="px-4 py-2.5 sf-mono">{r.working_hours ?? "—"}</td>
                        <td className="px-4 py-2.5"><StatusBadge status={r.status} /></td>
                        <td className="px-4 py-2.5 text-right"><ChevronRight className="w-4 h-4 text-zinc-300 inline" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {recentAmendments.length > 0 && (
            <div className="sf-surface mt-4" data-testid="bento-amendments-digest">
              <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
                <div className="flex items-center gap-2"><RefreshCw className="w-4 h-4 text-orange-600" /><h2 className="text-sm font-bold tracking-tight">Recent PO Amendments</h2><span className="text-[10px] text-zinc-400 uppercase tracking-wider">replaced / restored</span></div>
                <button onClick={() => navigate("/safety/po?amended=1")} className="text-xs sf-link-lateral" data-testid="amendments-open-all">View amended POs →</button>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-zinc-500">
                  <th className="px-4 py-2 sf-label font-semibold">PO</th><th className="px-4 py-2 sf-label font-semibold">Customer</th><th className="px-4 py-2 sf-label font-semibold">Change</th><th className="px-4 py-2 sf-label font-semibold">By</th><th className="px-4 py-2 sf-label font-semibold">When</th><th className="px-4 py-2 sf-label font-semibold">Review</th><th className="px-4 py-2"></th>
                </tr></thead>
                <tbody>
                  {recentAmendments.map((a) => {
                    const d = a.diff_summary || {};
                    const removed = (d.dropped_codes?.length || 0) + (d.kept_removed_codes?.length || 0);
                    return (
                      <tr key={a.po_id} className="border-t border-zinc-100 sf-row-clickable" onClick={() => navigate(`/safety/po/${a.po_id}/detail?tab=versions`)} data-testid={`amendment-row-${a.po_id}`}>
                        <td className="px-4 py-2.5"><span className="sf-mono font-medium">{a.po_number}</span><span className="ml-2 px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 text-[10px] font-semibold">{a.latest_source === "restore" ? "restored" : `v${(a.latest_version || 0) + 1}`}</span></td>
                        <td className="px-4 py-2.5 truncate max-w-[160px]">{a.customer_name || "—"}</td>
                        <td className="px-4 py-2.5 text-xs"><span className="text-emerald-700">+{d.added || 0}</span>{" · "}<span className="text-amber-700">{d.updated || 0}~</span>{" · "}<span className="text-red-700">-{removed}</span></td>
                        <td className="px-4 py-2.5 text-xs text-zinc-500 truncate max-w-[140px]">{a.latest_by || "—"}</td>
                        <td className="px-4 py-2.5 text-xs text-zinc-500">{fmtAgo(a.last_replaced_at)}</td>
                        <td className="px-4 py-2.5">{a.review_flagged ? <span className="sf-badge sf-badge-pending">needs review</span> : <span className="sf-badge sf-badge-approved">resolved</span>}</td>
                        <td className="px-4 py-2.5 text-right"><ChevronRight className="w-4 h-4 text-zinc-300 inline" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </SafetyShell>
  );
}

export default SafetyDashboard;
